import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiPlus, FiLoader, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { TAG_COLORS } from '../../components/Constantes/Colors';

function CategoryManager({ categories, setCategories, onClose }) {
    const { t } = useTranslation();
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editColor, setEditColor] = useState(1);
    const [inputValue, setInputValue] = useState('');
    const [inputColor, setInputColor] = useState(1);
    const [inputParentId, setInputParentId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [showPalette, setShowPalette] = useState(false);
    const [showEditPalette, setShowEditPalette] = useState(false);
    const [editParentId, setEditParentId] = useState(null);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const inputRef = useRef(null);
    const paletteRef = useRef(null);

    // Focus sur l'input à l'ouverture
    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    // Fermer la palette si on clique ailleurs
    useEffect(() => {
        function handleClickOutside(event) {
            if (paletteRef.current && !paletteRef.current.contains(event.target)) {
                setShowPalette(false);
                setShowEditPalette(false);
            }
        }
        if (showPalette || showEditPalette) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPalette, showEditPalette]);

    // Organiser les catégories en arbre
    const organizeCategories = () => {
        const rootCategories = categories.filter(cat => !cat.parent);
        const categoryMap = {};
        
        categories.forEach(cat => {
            categoryMap[cat.id] = { ...cat, children: [] };
        });

        categories.forEach(cat => {
            if (cat.parent && categoryMap[cat.parent.id]) {
                categoryMap[cat.parent.id].children.push(categoryMap[cat.id]);
            }
        });

        return rootCategories.map(cat => categoryMap[cat.id]);
    };

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleEdit = (cat) => {
        setEditId(cat.id);
        setEditName(cat.name);
        setEditColor(cat.color || 1);
        setEditParentId(cat.parent ? cat.parent.id : null);
    };

    const handleEditSave = async (id) => {
        setLoading(true);
        setActionId(id);
        try {
            await apiService.updateElementById('/employee/category', id, {
                name: editName,
                color: editColor,
                parent_id: editParentId || false,
            });
            setCategories(cats => cats.map(c => c.id === id ? {
                ...c,
                name: editName,
                color: editColor,
                parent: editParentId ? { id: editParentId, name: (cats.find(x => x.id === editParentId)?.name || '') } : null,
            } : c));
            notify(t('categoryManager.updated'), "", 2);
            setEditId(null);
        } catch {
            notify(t('categoryManager.updateError'), "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('categoryManager.deleteConfirm'))) return;
        setLoading(true);
        setActionId(id);
        try {
            await apiService.updateElementById('/employee/delete-category', id, {});
            setCategories(cats => cats.filter(c => c.id !== id));
            notify(t('categoryManager.deleted'), "", 2);
        } catch {
            notify(t('categoryManager.deleteError'), "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    const handleCreate = async () => {
        const name = inputValue.trim();
        if (!name) return;
        if (categories.some(cat => cat.name.toLowerCase() === name.toLowerCase())) {
            notify(t('categoryManager.alreadyExists'), "", 3);
            return;
        }
        setLoading(true);
        setActionId('create');
        try {
            const res = await apiService.saveElement('/employee/category', {
                name,
                color: inputColor,
                parent_id: inputParentId || null,
            });
            setCategories(cats => [...cats, res.data.result]);
            setInputValue('');
            setInputColor(1);
            setInputParentId(null);
            if (inputRef.current) inputRef.current.focus();
            notify(t('categoryManager.created'), "", 2);
        } catch {
            notify(t('categoryManager.createError'), "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    // Palette compacte en popover
    const ColorPopover = ({ selectedColor, setColor, onClose }) => (
        <div
            ref={paletteRef}
            className="absolute z-20 mt-2 p-3 bg-white border rounded-lg shadow-lg"
            style={{ minWidth: 160 }}
        >
            <div className="text-xs font-medium text-gray-600 mb-2">{t('categoryManager.chooseColor')}</div>
            <div className="grid grid-cols-4 gap-2">
                {TAG_COLORS.map((hex, idx) => (
                    <button
                        key={idx}
                        type="button"
                        className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all duration-150 hover:scale-110
                            ${selectedColor === idx + 1 ? 'border-gray-700 scale-110 shadow-md' : 'border-gray-200'}
                        `}
                        style={{ backgroundColor: hex }}
                        onClick={() => { setColor(idx + 1); onClose(); }}
                        aria-label={t('categoryManager.color', { n: idx + 1 })}
                        title={t('categoryManager.color', { n: idx + 1 })}
                    >
                        {selectedColor === idx + 1 && (
                            <FiCheck className="text-white drop-shadow" strokeWidth={3} />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderCategory = (cat, level = 0) => {
        const hasChildren = cat.children && cat.children.length > 0;
        const isExpanded = expandedIds.has(cat.id);
        const isEditing = editId === cat.id;

        return (
            <div key={cat.id} className="mb-1">
                <div
                    className={`flex items-center py-2 px-3 rounded-lg transition-all duration-150 
                        ${level > 0 ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}
                        ${isEditing ? 'ring-2 ring-blue-200' : ''}
                    `}
                    style={{ marginLeft: `${level * 20}px` }}
                >
                    {/* Toggle expand pour les parents */}
                    {hasChildren && !isEditing && (
                        <button
                            className="mr-2 text-gray-400 hover:text-gray-600 transition"
                            onClick={() => toggleExpand(cat.id)}
                        >
                            {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        </button>
                    )}
                    {!hasChildren && !isEditing && <div className="w-4 mr-2" />}

                    {/* Indicateur de couleur */}
                    <div className="relative mr-3">
                        <span
                            className="block w-6 h-6 rounded-lg border-2 border-white shadow-sm"
                            style={{ backgroundColor: TAG_COLORS[(cat.color || 1) - 1] }}
                        />
                        {level > 0 && (
                            <div className="absolute -left-2 top-1/2 w-2 h-0.5 bg-gray-300" />
                        )}
                    </div>

                    {isEditing ? (
                        <>
                            <input
                                className="input input-sm input-bordered flex-1 rounded-md"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                disabled={loading}
                                autoFocus
                            />
                            <select
                                className="select select-xs select-bordered ml-2 max-w-xs"
                                value={editParentId || ''}
                                onChange={e => setEditParentId(e.target.value ? Number(e.target.value) : null)}
                                disabled={loading}
                            >
                                <option value="">{t('categoryManager.root')}</option>
                                {categories
                                    .filter(c => c.id !== cat.id && !c.parent)
                                    .map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                            </select>
                            <div className="relative ml-2">
                                <button
                                    type="button"
                                    className="w-8 h-8 rounded-lg border-2 flex items-center justify-center hover:scale-110 transition"
                                    style={{ 
                                        backgroundColor: TAG_COLORS[editColor - 1],
                                        borderColor: '#e5e7eb'
                                    }}
                                    onClick={() => setShowEditPalette(v => !v)}
                                    disabled={loading}
                                />
                                {showEditPalette && (
                                    <ColorPopover
                                        selectedColor={editColor}
                                        setColor={c => setEditColor(c)}
                                        onClose={() => setShowEditPalette(false)}
                                    />
                                )}
                            </div>
                            <button
                                className="btn btn-xs btn-success ml-2 rounded-full"
                                onClick={() => handleEditSave(cat.id)}
                                disabled={loading || !editName.trim()}
                            >
                                {loading && actionId === cat.id ? <FiLoader className="animate-spin" /> : <FiCheck />}
                            </button>
                            <button
                                className="btn btn-xs btn-outline ml-1 rounded-full"
                                onClick={() => setEditId(null)}
                                disabled={loading}
                            >
                                <FiX />
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="flex-1 flex items-center gap-2">
                                <span className="font-medium text-gray-800">{cat.name}</span>
                                {hasChildren && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                        {cat.children.length}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                <button
                                    className="btn btn-xs btn-ghost rounded-full hover:bg-blue-100"
                                    onClick={() => handleEdit(cat)}
                                    disabled={loading}
                                    title={t('categoryManager.edit')}
                                >
                                    <FiEdit2 size={14} />
                                </button>
                                <button
                                    className="btn btn-xs btn-ghost rounded-full hover:bg-red-100 text-red-600"
                                    onClick={() => handleDelete(cat.id)}
                                    disabled={loading}
                                    title={t('categoryManager.delete')}
                                >
                                    {loading && actionId === cat.id ? (
                                        <FiLoader className="animate-spin" size={14} />
                                    ) : (
                                        <FiTrash2 size={14} />
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Enfants */}
                {hasChildren && isExpanded && !isEditing && (
                    <div className="ml-4 border-l-2 border-gray-200 pl-2 mt-1">
                        {cat.children.map(child => renderCategory(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const organizedCategories = organizeCategories();

    return (
        <div className="p-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[--color-primary]">{t('categoryManager.title')}</h2>
                <button
                    className="btn btn-xs btn-outline text-[--color-orange] border-[--color-orange] hover:bg-[--color-orange]/10 rounded-full"
                    onClick={onClose}
                >
                    {t('categoryManager.close')}
                </button>
            </div>

            {/* Formulaire de création */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex gap-2 items-start">
                    <div className="relative flex-1">
                        <input
                            ref={inputRef}
                            className="input input-sm input-bordered p-3 w-full rounded-md"
                            type="text"
                            placeholder={t('categoryManager.newTagPlaceholder')}
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            disabled={loading}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                        />
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg border-2 flex items-center justify-center hover:scale-110 transition"
                            style={{ 
                                backgroundColor: TAG_COLORS[inputColor - 1],
                                borderColor: '#e5e7eb'
                            }}
                            onClick={() => setShowPalette(v => !v)}
                            disabled={loading}
                        />
                        {showPalette && (
                            <ColorPopover
                                selectedColor={inputColor}
                                setColor={c => setInputColor(c)}
                                onClose={() => setShowPalette(false)}
                            />
                        )}
                    </div>
                    <button
                        className="btn btn-sm bg-[--color-primary] text-white rounded-md px-4 hover:bg-[--bg-hover]"
                        onClick={handleCreate}
                        disabled={loading || !inputValue.trim()}
                    >
                        {loading && actionId === 'create' ? (
                            <FiLoader className="animate-spin" />
                        ) : (
                            <FiPlus />
                        )}
                        {t('categoryManager.add')}
                    </button>
                </div>
                <div className="mt-3">
                    <select
                        className="select select-sm select-bordered w-full"
                        value={inputParentId || ''}
                        onChange={e => setInputParentId(e.target.value ? Number(e.target.value) : null)}
                        disabled={loading}
                    >
                        <option value="">{t('categoryManager.rootCategory')}</option>
                        {categories.filter(c => !c.parent).map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Liste hiérarchique */}
            <div className="flex-1 overflow-y-auto">
                <div className="space-y-1 group">
                    {organizedCategories.length === 0 ? (
                        <div className="text-center text-gray-400 py-8">
                            {t('categoryManager.empty')}
                        </div>
                    ) : (
                        organizedCategories.map(cat => renderCategory(cat))
                    )}
                </div>
            </div>
        </div>
    );
}

export default CategoryManager;